import * as React from 'react';
import { observable, makeObservable } from 'mobx';
import { WorkflowConfiguration } from 'nomst/Stores/Configuration/workflowConfiguration';
import { RuleField, RuleCode } from 'typings/enums';
import './workflowMenu.scss';

export interface IWorkflowMenuProps {
    workflowConfiguration: WorkflowConfiguration;
}

export interface IWorkflowMenuState {
    isMenuVisible: boolean;
    submenu: Nullable<RuleCode>;
}

export default class WorkflowMenu extends React.PureComponent<IWorkflowMenuProps, IWorkflowMenuState> {
    @observable private menuElement: Nullable<Element>;

    constructor(props: IWorkflowMenuProps) {
        super(props);
        makeObservable(this);
        this.state = {
            isMenuVisible: false,
            submenu: null
        };

        this.toggleIsMenuVisible = this.toggleIsMenuVisible.bind(this);
        this.hasFocus = this.hasFocus.bind(this);
        this.handleMouseEvent = this.handleMouseEvent.bind(this);
        this.handleToggleHrMenu = this.handleToggleHrMenu.bind(this);
        this.handleToggleHierarchyMenu = this.handleToggleHierarchyMenu.bind(this);
        this.handleToggleLeaderMenu = this.handleToggleLeaderMenu.bind(this);
    }

    componentDidMount(): void {
        document.addEventListener('mouseup', this.handleMouseEvent);
    }

    componentWillUnmount(): void {
        document.removeEventListener('mouseup', this.handleMouseEvent);
    }

    render(): React.ReactNode {
        return (
            <div
                className="nomst-workflow-menu"
                ref={(element) => this.menuElement = element}
            >
                <button
                    onClick={this.toggleIsMenuVisible}
                    className="no-btn-style nomst-workflow-menu__toggle-menu-btn"
                    id="plus-btn"
                >   <i className="icon-border icon-circle-thin">
                        <i className="icon-plus" />
                    </i>
                    Add
                </button>
                <div className="nomst-workflow-menu__menu-wrapper" hidden={!this.state.isMenuVisible}>
                    {this.renderMainMenu()}
                    <div className={this.submenuClasses}>
                        {this.renderHrSubMenu()}
                        {this.renderHierarchySubMenu()}
                        {this.renderLeaderSubMenu()}
                    </div>
                </div>
            </div>
        );
    }

    private renderMainMenu(): React.ReactNode {
        const { workflowConfiguration } = this.props;
        const directMgrRule = { code: RuleCode.ImmediateManager };
        return (
            <div className="nomst-workflow-menu__mainmenu">
                <h3 className="nomst-workflow-menu__mainmenu-headline">Add Reviewer</h3>
                <ul className="nomst-workflow-menu__list">
                    <li className="nomst-workflow-menu__has-submenu">
                        <button
                            className={this.getMenuButtonClasses(RuleCode.HumanResourceReviewer)}
                            onClick={this.handleToggleHrMenu}
                        >
                            HR Roles
                        </button>
                    </li>
                    <li className="nomst-workflow-menu__has-submenu">
                        <button
                            className={this.getMenuButtonClasses(RuleCode.FollowHierarchy)}
                            onClick={this.handleToggleHierarchyMenu}
                        >
                            Follow Hierarchy to
                        </button>
                    </li>
                    <li className="nomst-workflow-menu__has-submenu">
                        <button
                            className={this.getMenuButtonClasses(RuleCode.GoToFieldLeader)}
                            onClick={this.handleToggleLeaderMenu}
                        >
                            Head of
                        </button>
                    </li>
                    {this.renderMenu(
                        'Direct Manager',
                        'direct-manager',
                        () => workflowConfiguration.addRule(directMgrRule),
                        false,
                        workflowConfiguration.hasRule(directMgrRule))}
                </ul>
            </div>
        );
    }

    private renderHrSubMenu(): React.ReactNode {
        const { workflowConfiguration } = this.props;
        const hrDeptRule = { code: RuleCode.HumanResourceReviewer, field: RuleField.Department };
        const hrDivisionRule = { code: RuleCode.HumanResourceReviewer, field: RuleField.Division };
        const hrBusinessUnitRule = { code: RuleCode.HumanResourceReviewer, field: RuleField.BusinessUnit };
        return (
            <ul
                className="nomst-workflow-menu__submenu"
                hidden={this.shouldHideSubMenu(RuleCode.HumanResourceReviewer)}
            >
                {this.renderMenu(
                    'HR Administrator',
                    'hr-administrator',
                    () => workflowConfiguration.addRule({ code: RuleCode.HumanResourceApprover }),
                    false,
                    workflowConfiguration.hasHrApproverRule)}
                {this.renderMenu(
                    'Department HR Business Partner',
                    'department-hrbp',
                    () => workflowConfiguration.addRule(hrDeptRule),
                    !workflowConfiguration.hasDepartment,
                    workflowConfiguration.hasRule(hrDeptRule))}
                {this.renderMenu(
                    'Division HR Business Partner',
                    'division-hrbp',
                    () => workflowConfiguration.addRule(hrDivisionRule),
                    !workflowConfiguration.hasDivision,
                    workflowConfiguration.hasRule(hrDivisionRule))}
                {this.renderMenu(
                    'Business Unit HR Business Partner',
                    'business-unit-hrbp',
                    () => workflowConfiguration.addRule(hrBusinessUnitRule),
                    !workflowConfiguration.hasBusinessUnit,
                    workflowConfiguration.hasRule(hrBusinessUnitRule))}
            </ul>
        );
    }

    private renderHierarchySubMenu(): React.ReactNode {
        const { workflowConfiguration } = this.props;
        const hierarchyDeptRule = { code: RuleCode.FollowHierarchy, field: RuleField.Department };
        const hierarchyDivisionRule = { code: RuleCode.FollowHierarchy, field: RuleField.Division };
        const hierarchyBusinessUnitRule = { code: RuleCode.FollowHierarchy, field: RuleField.BusinessUnit };
        const hierarchyLevelBelowRule = { code: RuleCode.FollowHierarchy, field: RuleField.LevelBelowCeo };
        return (
            <ul
                className="nomst-workflow-menu__submenu"
                hidden={this.shouldHideSubMenu(RuleCode.FollowHierarchy)}
            >
                {this.renderMenu(
                    'Department',
                    'department',
                    () => workflowConfiguration.addRule(hierarchyDeptRule),
                    !workflowConfiguration.hasDepartment,
                    workflowConfiguration.hasRule(hierarchyDeptRule))}
                {this.renderMenu(
                    'Division',
                    'division',
                    () => workflowConfiguration.addRule(hierarchyDivisionRule),
                    !workflowConfiguration.hasDivision,
                    workflowConfiguration.hasRule(hierarchyDivisionRule))}
                {this.renderMenu(
                    'Business Unit',
                    'business-unit',
                    () => workflowConfiguration.addRule(hierarchyBusinessUnitRule),
                    !workflowConfiguration.hasBusinessUnit,
                    workflowConfiguration.hasRule(hierarchyBusinessUnitRule))}
                {this.renderMenu(
                    'Level below CEO',
                    'level-below-ceo',
                    () => workflowConfiguration.addRule(hierarchyLevelBelowRule),
                    false,
                    workflowConfiguration.hasRule(hierarchyLevelBelowRule))}
                {this.renderMenu(
                    'CEO',
                    'ceo',
                    () => workflowConfiguration.addRule({ code: RuleCode.FollowHierarchy }),
                    false,
                    workflowConfiguration.hasRule({ code: RuleCode.FollowHierarchy }))}
            </ul>
        );
    }

    private renderLeaderSubMenu(): React.ReactNode {
        const { workflowConfiguration } = this.props;
        const leaderDeptRule = { code: RuleCode.GoToFieldLeader, field: RuleField.Department };
        const leaderDivisionRule = { code: RuleCode.GoToFieldLeader, field: RuleField.Division };
        const leaderBusinessUnitRule = { code: RuleCode.GoToFieldLeader, field: RuleField.BusinessUnit };
        const leaderCeoRule = { code: RuleCode.GoToFieldLeader, field: RuleField.Ceo };
        return (
            <ul
                className="nomst-workflow-menu__submenu"
                hidden={this.shouldHideSubMenu(RuleCode.GoToFieldLeader)}
            >
                {this.renderMenu(
                    'Department',
                    'department-head',
                    () => workflowConfiguration.addRule(leaderDeptRule),
                    !workflowConfiguration.hasDepartment,
                    workflowConfiguration.hasRule(leaderDeptRule))}
                {this.renderMenu(
                    'Division',
                    'division-head',
                    () => workflowConfiguration.addRule(leaderDivisionRule),
                    !workflowConfiguration.hasDivision,
                    workflowConfiguration.hasRule(leaderDivisionRule))}
                {this.renderMenu(
                    'Business Unit',
                    'business-unit-head',
                    () => workflowConfiguration.addRule(leaderBusinessUnitRule),
                    !workflowConfiguration.hasBusinessUnit,
                    workflowConfiguration.hasRule(leaderBusinessUnitRule))}
                {this.renderMenu(
                    'CEO',
                    'ceo',
                    () => workflowConfiguration.addRule(leaderCeoRule),
                    false,
                    workflowConfiguration.hasRule(leaderCeoRule))}
            </ul>
        );
    }

    private renderMenu(
        title: string,
        className: string,
        onClick: () => void,
        hidden: boolean = false,
        disabled: boolean = false
    ): React.ReactNode {
        return (
            <li>
                <button
                    className={`nomst-workflow-menu__btn nomst-workflow-menu__btn--${className} no-btn-style`}
                    hidden={hidden}
                    disabled={disabled}
                    /* tslint:disable-next-line:jsx-no-lambda need to disable this since it's a few layers deep */
                    onClick={() => !disabled && this.handleMenuClick(onClick)}
                >
                    {title}
                </button>
            </li>
        );
    }

    private getMenuButtonClasses(menu: RuleCode): string {
        let className = '';
        switch (menu) {
            case RuleCode.HumanResourceReviewer:
                className = 'nomst-workflow-menu__btn nomst-workflow-menu__btn--hr-roles';
                break;
            case RuleCode.FollowHierarchy:
                className = 'nomst-workflow-menu__btn nnomst-workflow-menu__btn--follow-hierarchy';
                break;
            case RuleCode.GoToFieldLeader:
                className = 'nomst-workflow-menu__btn nomst-workflow-menu__btn--head-of';
                break;
        }

        return `${className} no-btn-style ${this.state.submenu === menu ? 'nomst-workflow-menu__btn--selected' : ''}`;
    }

    private get submenuClasses(): string {
        return this.state.submenu
            ? 'nomst-workflow-menu__submenus nomst-workflow-menu__submenus--show'
            : 'nomst-workflow-menu__submenus';
    }

    private toggleIsMenuVisible(): void {
        const { isMenuVisible } = this.state;
        this.setState({ isMenuVisible: !isMenuVisible });
    }

    private toggleSubMenu(menu: RuleCode): void {
        const { submenu } = this.state;
        if (submenu === menu) {
            this.setState({ submenu: null });
            return;
        }

        this.setState({ submenu: menu });
    }

    private shouldHideSubMenu(menu: RuleCode): boolean {
        const { submenu } = this.state;
        if (!submenu) {
            return true;
        }

        return submenu !== menu;
    }

    private handleToggleHrMenu(): void {
        this.toggleSubMenu(RuleCode.HumanResourceReviewer);
    }

    private handleToggleHierarchyMenu(): void {
        this.toggleSubMenu(RuleCode.FollowHierarchy);
    }

    private handleToggleLeaderMenu(): void {
        this.toggleSubMenu(RuleCode.GoToFieldLeader);
    }

    private handleMenuClick(delegatedEvent: () => void): void {
        delegatedEvent();
        this.setState({ isMenuVisible: false, submenu: null });
    }

    private hasFocus(target: Nullable<EventTarget>): boolean {
        // when component unmounts, ref becomes null
        if (!this.menuElement) {
            return false;
        }

        const nodeIterator = document.createNodeIterator(this.menuElement, NodeFilter.SHOW_ELEMENT);
        let hasFocus = false;
        let node: Nullable<Node>;

        if (nodeIterator.root === target) {
            return true;
        }

        node = nodeIterator.nextNode();
        // walk down the tree of elements from the root dropdown div
        // and see if the target of the event is a child;
        // if so, it still has focus so don't close it yet
        while (node) {
            if (node === target) {
                hasFocus = true;
                break;
            }
            node = nodeIterator.nextNode();
        }

        return hasFocus;
    }

    private handleMouseEvent(event: Event): void {
        if (this.hasFocus(event.target) === false) {
            this.setState({ isMenuVisible: false });
        }
    }
}